import { ColumnConfigModel, getColumnConfig } from './columnConfig'

// Used as the canonical, rollover-safe identifier on persisted views and the API contract.
export const CLASSES = ['senior', 'junior', 'sophomore', 'freshman'] as const
export type Class = (typeof CLASSES)[number]

export const CLASS_LABELS: Record<Class, string> = {
  senior: 'Senior',
  junior: 'Junior',
  sophomore: 'Sophomore',
  freshman: 'Freshman',
}

export const CLASS_ABBREVIATIONS: Record<Class, string> = {
  senior: 'Sr',
  junior: 'Jr',
  sophomore: 'So',
  freshman: 'Fr',
}

const CLASS_GRAD_YEAR_OFFSETS: Record<Class, number> = {
  senior: 0,
  junior: 1,
  sophomore: 2,
  freshman: 3,
}

// Frontend year-resolution is for cell-value display only. The API contract uses class names.
export const getGradYearForClass = (cls: Class, currentSeniorYear: number): number =>
  currentSeniorYear + CLASS_GRAD_YEAR_OFFSETS[cls]

export const sumByClasses = (
  byGradYear: Record<string, number> | undefined,
  selected: Class[],
  currentSeniorYear: number,
): number | undefined => {
  if (!byGradYear) return undefined
  return selected.reduce((sum, cls) => sum + (byGradYear[String(getGradYearForClass(cls, currentSeniorYear))] ?? 0), 0)
}

export const formatSelectedClassesLabel = (selected: Class[]): string => {
  if (selected.length === 0) return 'No Filters'
  return CLASSES.filter((c) => selected.includes(c))
    .map((c) => CLASS_ABBREVIATIONS[c])
    .join(' • ')
}

export const HANDRAISER_COLUMN_FIELD = 'numHandraisers'

export type HandraiserColumnConfig = { classes?: Class[] }

export const getHandraiserClasses = (model: ColumnConfigModel | undefined): Class[] => {
  const cfg = getColumnConfig<HandraiserColumnConfig>(model, HANDRAISER_COLUMN_FIELD)
  return cfg?.classes ?? [...CLASSES]
}

export const FOLLOWER_COLUMN_FIELD = 'numFollowers'

export type FollowerColumnConfig = { classes?: Class[] }

export const getFollowerClasses = (model: ColumnConfigModel | undefined): Class[] => {
  const cfg = getColumnConfig<FollowerColumnConfig>(model, FOLLOWER_COLUMN_FIELD)
  return cfg?.classes ?? [...CLASSES]
}

// Used as the canonical, rollover-safe identifier on persisted views and the API contract
// for historic follower cohorts. Year1 = most recent alumni cohort (senior-1), Year2 = the cohort before that.
export const HISTORIC_FOLLOWER_YEARS = ['year1', 'year2'] as const
export type HistoricFollowerYear = (typeof HISTORIC_FOLLOWER_YEARS)[number]

const HISTORIC_FOLLOWER_SENIOR_OFFSETS: Record<HistoricFollowerYear, number> = {
  year1: 1,
  year2: 2,
}

export const getHistoricFollowerGradYear = (year: HistoricFollowerYear, currentSeniorYear: number): number =>
  currentSeniorYear - HISTORIC_FOLLOWER_SENIOR_OFFSETS[year]

export const getHistoricFollowerLabel = (year: HistoricFollowerYear, currentSeniorYear: number): string =>
  `${getHistoricFollowerGradYear(year, currentSeniorYear)} Alumni`

export const sumHistoricFollowersByYears = (
  byGradYear: Record<string, number> | undefined,
  selected: HistoricFollowerYear[],
  currentSeniorYear: number,
): number | undefined => {
  if (!byGradYear) return undefined
  return selected.reduce(
    (sum, y) => sum + (byGradYear[String(getHistoricFollowerGradYear(y, currentSeniorYear))] ?? 0),
    0,
  )
}

export const formatSelectedHistoricFollowerYearsLabel = (
  selected: HistoricFollowerYear[],
  currentSeniorYear: number,
): string => {
  if (selected.length === 0) return 'No Filters'
  return HISTORIC_FOLLOWER_YEARS.filter((y) => selected.includes(y))
    .map((y) => getHistoricFollowerGradYear(y, currentSeniorYear))
    .join(' • ')
}

export const HISTORIC_FOLLOWER_COLUMN_FIELD = 'numHistoricFollowers'

export type HistoricFollowerColumnConfig = { years?: HistoricFollowerYear[] }

export const getHistoricFollowerYears = (model: ColumnConfigModel | undefined): HistoricFollowerYear[] => {
  const cfg = getColumnConfig<HistoricFollowerColumnConfig>(model, HISTORIC_FOLLOWER_COLUMN_FIELD)
  return cfg?.years ?? [...HISTORIC_FOLLOWER_YEARS]
}
